import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { animate, style, transition, trigger } from '@angular/animations';
import { Subscription } from 'rxjs';
import { SnackbarService } from 'src/app/core/services/snackbar.service';

interface SnackNotification {
  id: number;
  message: string;
  type: 'error' | 'success' | 'warning';
  timeoutId?: number;
  createdAt: number;
}

@Component({
  selector: 'app-stacked-snackbar-container',
  imports: [CommonModule],
  templateUrl: './stacked-snackbar-container.component.html',
  styleUrls: ['./stacked-snackbar-container.component.scss'],
  animations: [
    trigger('slideIn', [
      transition(':enter', [
        style({ transform: 'translateX(100%)', opacity: 0 }),
        animate('300ms ease-out', style({ transform: 'translateX(0)', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-in', style({ transform: 'translateX(100%)', opacity: 0 }))
      ])
    ])
  ]
})
export class StackedSnackbarContainerComponent implements OnInit, OnDestroy {
  notifications: SnackNotification[] = [];
  private subscriptions = new Subscription();
  private counter = 0;
  
  constructor(private snackbarService: SnackbarService) {}
  
  ngOnInit() {
    this.subscriptions.add(
      this.snackbarService.notificationSubject.subscribe(notification => {
        this.addNotification(notification);
      })
    );
  }
  
  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
  
  addNotification(notification: { message: string, type: 'error' | 'success' | 'warning' }) {
    // Check for duplicates within a short time window
    const now = Date.now();
    const isDuplicate = this.notifications.some(n => 
      n.message === notification.message && 
      (now - n.createdAt) < 1000 // 1 second deduplication window
    );
    
    if (isDuplicate) {
      return; // Don't add duplicate notifications
    }
    
    const id = this.counter++;
    const newNotification: SnackNotification = {
      id,
      message: notification.message,
      type: notification.type,
      createdAt: now // Store creation timestamp
    };
    
    // Add to array
    this.notifications.push(newNotification);
    
    // Set timeout to remove after 5 seconds
    const timeoutId = window.setTimeout(() => {
      this.removeNotification(id);
    }, 5000);
    
    // Store the timeout ID so we can clear it if needed
    newNotification.timeoutId = timeoutId;
  }
  
  removeNotification(id: number) {
    const index = this.notifications.findIndex(n => n.id === id);
    if (index > -1) {
      // Clear timeout if it exists
      if (this.notifications[index].timeoutId) {
        clearTimeout(this.notifications[index].timeoutId);
      }
      
      // Remove from array
      this.notifications.splice(index, 1);
    }
  }
} 